import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen";
import {
  QuestionCheckResult,
  SurveyCheckResult,
} from "@/features/survey_sync/types/syncCheckResult";
import {
  SurveyTemplateWithQuestions,
  SurveyToCheckWithQuestionsAndResponses,
} from "..";
import { checkForInvalidConditionalReferences } from "./checkForInvalidConditionalReferences";
import { checkForSurveyPropertyChanges } from "./checkForSurveyPropertyChanges";
import { checkIfConditionalsChanged } from "./checkIfConditionalsChanged";
import { checkIfOriginalWasEdited } from "./checkIfOriginalWasEdited";
import { checkIfQuestionFieldPropertiesChanged } from "./checkIfQuestionFieldPropertiesChanged";
import { checkIfQuestionTypeChanged } from "./checkIfQuestionTypeChanged";
import { checkInstanceFieldOptionsRisk } from "./checkInstanceFieldOptionsRisk";
import {
  checkForMissingQuestionsInInstance,
  checkIfQuestionNotInTemplate,
} from "./checkQuestionTemplateAlignment";
import { determineLinkedQuestions } from "./determineLinkedQuestions";

export function checkSurveyForDangerousChanges(
  survey: SurveyToCheckWithQuestionsAndResponses,
  surveyTemplate: SurveyTemplateWithQuestions
): SurveyCheckResult {
  const questionResults: QuestionCheckResult[] = [];

  const surveyQuestions = survey.question || [];
  const templateQuestions = surveyTemplate.question || [];

  const missingQuestionsInInstance = checkForMissingQuestionsInInstance(
    surveyQuestions,
    templateQuestions
  );

  const existingQuestionResults: QuestionCheckResult[] = surveyQuestions.map(
    (question) => {
      // const doesQuestionHaveResponses = checkIfQuestionsHasResponses(question);

      // if (!doesQuestionHaveResponses) {
      //   return {
      //     id: question.id,
      //     question: question.question,
      //     dangerousChanges: [],
      //   };
      // }

      // Find the corresponding template question
      const templateQuestion = templateQuestions.find(
        (tq) => tq.id === question.survey_bank_question_bank_ref_id
      );

      const allChecks = [
        checkIfQuestionNotInTemplate(question, templateQuestions),
        templateQuestion
          ? checkIfOriginalWasEdited(
              question,
              templateQuestion,
              surveyQuestions
            )
          : false,
        templateQuestion
          ? checkIfQuestionTypeChanged(question, templateQuestion)
          : false,
        templateQuestion
          ? checkIfQuestionFieldPropertiesChanged(question, templateQuestion)
          : false,
        templateQuestion
          ? checkInstanceFieldOptionsRisk(question, templateQuestion)
          : false,
        templateQuestion
          ? checkIfConditionalsChanged(
              question,
              templateQuestion,
              surveyQuestions
            )
          : false,
        checkForInvalidConditionalReferences(question, surveyQuestions),
      ];

      const dangerousChanges = allChecks.filter(
        (change): change is Exclude<typeof change, false> => change !== false
      );

      return {
        instanceQuestion: question,
        templateQuestion: templateQuestion || null,
        dangerousChanges,
      };
    }
  );

  questionResults.push(...existingQuestionResults);

  missingQuestionsInInstance.forEach((missingChange) => {
    questionResults.push({
      instanceQuestion: null,
      templateQuestion: missingChange.context.templateQuestion,
      dangerousChanges: [missingChange],
    });
  });

  const surveyLevelChanges = checkForSurveyPropertyChanges(
    survey,
    surveyTemplate
  );

  // Determine linked questions that must be synced together
  const linkedQuestions = determineLinkedQuestions(
    templateQuestions,
    surveyQuestions,
    questionResults
  );
  const questionsWithDangerousChanges = questionResults.filter(
    (qr) => qr.dangerousChanges.length > 0
  );
  const questionsToSync = questionsWithDangerousChanges.filter((qr) =>
    qr.dangerousChanges.some((dc) => dc.type !== "QUESTION_NOT_IN_TEMPLATE")
  );
  const questionsToDelete = questionsWithDangerousChanges.filter((qr) =>
    qr.dangerousChanges.some((dc) => dc.type === "QUESTION_NOT_IN_TEMPLATE")
  );

  return {
    surveyInstance: survey,
    surveyTemplate: surveyTemplate,
    allInstanceQuestions: surveyQuestions,
    allTemplateQuestions: templateQuestions,
    templateCrmSystem: surveyTemplate.crm_system || DEFAULT_CRM_SYSTEM,
    instanceCrmSystem: survey?.crm?.crm_system || DEFAULT_CRM_SYSTEM,
    questionsToSync: questionsToSync,
    questionsToDelete: questionsToDelete,
    surveyLevelChanges:
      surveyLevelChanges.length > 0 ? surveyLevelChanges : undefined,
    linkedQuestions,
  };
}
